import React, { useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

const TIMES = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00"];

export default function CallbackForm() {
  const { lang } = useI18n();
  const today = new Date();
  const tomorrow = new Date(today.getTime() + 24 * 3600 * 1000);
  const defaultDate = tomorrow.toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    preferred_date: defaultDate,
    preferred_time: "10:00",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/callbacks", { ...form, language: lang });
      setDone(true);
      toast.success(lang === "fr" ? "Nous vous rappelons." : "We'll call you back.");
    } catch {
      toast.error(lang === "fr" ? "Une erreur est survenue." : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white border border-[#D4AF37]/40 p-8 text-center" data-testid="callback-done">
        <div className="overline text-[#D4AF37]">{lang === "fr" ? "Demande envoyée" : "Request sent"}</div>
        <h3 className="font-serif text-3xl text-[#0A1128] mt-3">
          {lang === "fr" ? "À très vite au téléphone." : "Talk to you soon."}
        </h3>
        <p className="mt-4 text-sm text-[#0A1128]/70">
          {lang === "fr" ? "Un membre de l'équipe vous appellera au créneau choisi." : "A team member will call you at the chosen slot."}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#0A1128]/10 p-8" data-testid="callback-form">
      <div className="overline text-[#D4AF37]">{lang === "fr" ? "Rappel gratuit" : "Free callback"}</div>
      <h3 className="font-serif text-3xl text-[#0A1128] mt-2">
        {lang === "fr" ? "Vous préférez un appel ?" : "Prefer a phone call?"}
      </h3>
      <p className="text-sm text-[#0A1128]/70 mt-2">
        {lang === "fr"
          ? "Laissez-nous votre numéro et un créneau — nous vous rappelons."
          : "Leave your number and a slot — we'll call you back."}
      </p>
      <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="overline text-[#0A1128]/60">{lang === "fr" ? "Nom" : "Name"}</label>
          <input required value={form.name} onChange={set("name")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-2 focus:outline-none focus:border-[#D4AF37]" data-testid="callback-name" />
        </div>
        <div>
          <label className="overline text-[#0A1128]/60">{lang === "fr" ? "Téléphone" : "Phone"}</label>
          <input required type="tel" value={form.phone} onChange={set("phone")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-2 focus:outline-none focus:border-[#D4AF37]" data-testid="callback-phone" />
        </div>
        <div>
          <label className="overline text-[#0A1128]/60">{lang === "fr" ? "Date" : "Date"}</label>
          <input required type="date" min={defaultDate} value={form.preferred_date} onChange={set("preferred_date")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-2 focus:outline-none focus:border-[#D4AF37]" data-testid="callback-date" />
        </div>
        <div>
          <label className="overline text-[#0A1128]/60">{lang === "fr" ? "Horaire" : "Time"}</label>
          <select value={form.preferred_time} onChange={set("preferred_time")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-2 focus:outline-none focus:border-[#D4AF37]" data-testid="callback-time">
            {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="overline text-[#0A1128]/60">{lang === "fr" ? "Sujet (facultatif)" : "Topic (optional)"}</label>
          <textarea rows={2} value={form.notes} onChange={set("notes")} className="w-full bg-transparent border-b border-[#0A1128]/30 py-2 focus:outline-none focus:border-[#D4AF37] resize-none" data-testid="callback-notes" />
        </div>
        <div className="md:col-span-2">
          <button type="submit" disabled={loading} className="bg-[#0A1128] text-[#F9F8F6] px-8 py-3 text-xs tracking-[0.24em] uppercase hover:bg-[#D4AF37] hover:text-[#0A1128] transition-colors duration-500 disabled:opacity-60" data-testid="callback-submit">
            {loading ? "..." : (lang === "fr" ? "Demander un rappel" : "Request callback")}
          </button>
        </div>
      </form>
    </div>
  );
}
